// src/containers/AuthenticationPage/GetStartedForm/GetStartedForm.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { START_DATE } from '../../../util/dates';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldSelect,
  FieldBoolean,
  FieldCheckbox,
  Button,
} from '../../../components';

import css from './GetStartedForm.module.css';

// Complete list of U.S. states
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// Define practice types
const practiceTypeOptions = [
  { value: 'IV_Infusion', label: 'IV Infusion' },
  { value: 'Hormone_Optimization', label: 'Hormone Optimization' },
  { value: 'Medical_Aesthetics', label: 'Medical Aesthetics' },
  { value: 'Urgent_Care', label: 'Urgent Care' },
  { value: 'Primary_Care', label: 'Primary Care' },
  { value: 'Medical_Weight_Loss', label: 'Medical Weight Loss' },
  { value: 'Mental_Health', label: 'Mental Health' },
  { value: 'Mens_Health_TRT', label: "Men's Health TRT" },
  { value: 'Womens_Health', label: "Women's Health" },
];

// Add helper function to get step names
const getStepName = (stepNumber) => {
  const stepNames = {
    0: 'practice_types',
    1: 'state_selection',
    2: 'license_type',
    3: 'controlled_substances',
    4: 'start_date',
    5: 'personal_info',
    6: 'registration'
  };
  return stepNames[stepNumber] || `unknown_step_${stepNumber}`;
};

// Add tracking function
const trackStepCompletion = (stepNumber, additionalData = {}) => {
  const eventName = getStepName(stepNumber);
  const timestamp = new Date().toISOString();
  const sessionId = localStorage.getItem('session_id') || Date.now();

  // Store session ID if not already stored
  if (!localStorage.getItem('session_id')) {
    localStorage.setItem('session_id', sessionId);
  }

  if (window.gtag) {
    window.gtag('event', eventName, {
      // Basic tracking info
      step_number: stepNumber,
      form_name: 'get_started',
      timestamp: timestamp,
      session_id: sessionId,
      user_flow: 'onboarding',

      // Time tracking
      time_on_step: additionalData.timeSpent || 0,
      total_time: additionalData.totalTime || 0,
      
      // User interaction data
      field_changes: additionalData.fieldChanges || 0,
      validation_errors: additionalData.validationErrors || 0,
      
      // Navigation data
      previous_step: additionalData.previousStep,
      next_step: additionalData.nextStep,
      
      // Device/browser info
      screen_size: `${window.innerWidth}x${window.innerHeight}`,
      device_type: getDeviceType(),
      
      // Form completion metrics
      step_completion_percentage: ((stepNumber / 6) * 100).toFixed(2),
      is_final_step: stepNumber === 6
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('Enhanced event tracked:', {
        name: eventName,
        step: stepNumber,
        ...additionalData
      });
    }
  }

  // Add Facebook Pixel tracking
  if (window.fbq) {
    // Track each step
    window.fbq('track', 'GetStartedStep', {
      step_number: stepNumber,
      step_name: eventName,
      completion_percentage: ((stepNumber / 6) * 100).toFixed(2)
    });

    // If this is the final step, track it as a Lead conversion
    if (stepNumber === 6) {
      window.fbq('track', 'Lead', {
        content_name: 'GetStarted Completion',
        status: 'complete',
        value: 1.00,
        currency: 'USD'
      });
    }
  }
};

// Add this helper function to detect device type
const getDeviceType = () => {
  const width = window.innerWidth;
  if (width <= 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

// Add phone validation helper
const validatePhone = (value) => {
  if (!value) return undefined; // Optional field
  const digits = value.replace(/\D/g, '');
  if (digits.length !== 10) {
    return 'Phone number must be 10 digits';
  }
  return undefined;
};

// Add SMS consent disclaimer component
const SmsConsentDisclaimer = () => (
  <div className={css.smsConsentDisclaimer}>
    Providing your number means you consent to SMS messages. Rates may apply. STOP to opt out.
  </div>
);

// Add this helper function to render the card with header elements
const renderCard = (content, cardTitle, stepCounter, step, setStep) => (
  <div className={css.card}>
    <div className={css.progressBar}>
      <div 
        className={css.progressFill} 
        style={{ width: `${((step + 1) / 7) * 100}%` }} 
      />
    </div>
    <div className={css.cardHeader}>
      {step > 0 && (
        <button
          type="button"
          className={css.backButton}
          onClick={() => setStep(step - 1)}
        >
          ← Back
        </button>
      )}
      <div className={css.stepCounter}>{stepCounter}</div>
    </div>
    <h2 className={css.cardTitle}>{cardTitle}</h2>
    {content}
  </div>
);

const GetStartedFormComponent = (props) => {
  const { intl, onSubmit, termsAndConditions, userFields, userTypes, isSubmitting } = props;

  const [step, setStep] = useState(0);
  const [initialValues, setInitialValues] = useState({});

  const passwordValidators = validators.composeValidators(
    validators.required(intl.formatMessage({ id: 'GetStartedForm.passwordRequired' })),
    validators.minLength(
      intl.formatMessage({ id: 'GetStartedForm.passwordTooShort' }),
      validators.PASSWORD_MIN_LENGTH
    )
  );

  const renderCard = (content, cardTitle, stepCounter, step, setStep) => (
    <div className={css.card}>
      <div className={css.progressBar}>
        <div 
          className={css.progressFill} 
          style={{ width: `${((step + 1) / 7) * 100}%` }} 
        />
      </div>
      <div className={css.cardHeader}>
        {step > 0 && (
          <button
            type="button"
            className={css.backButton}
            onClick={() => setStep(step - 1)}
          >
            ← Back
          </button>
        )}
        <div className={css.stepCounter}>{stepCounter}</div>
      </div>
      <h2 className={css.cardTitle}>{cardTitle}</h2>
      {content}
    </div>
  );

  const handleFieldValidation = (fieldName) => {
    switch (fieldName) {
      case 'pub_practice_types':
        return (value) => {
          if (!value || value.length === 0) {
            return intl.formatMessage({ id: 'GetStartedForm.pub_practice_typesRequired' });
          }
          return undefined;
        };
      case 'pub_State':
        return validators.required(intl.formatMessage({ id: 'GetStartedForm.pub_StateRequired' }));
      case 'pub_License':
        return validators.required(intl.formatMessage({ id: 'GetStartedForm.pub_LicenseRequired' }));
      case 'pub_controlled_substances':
        return validators.required(intl.formatMessage({ id: 'GetStartedForm.pub_controlled_substancesRequired' }));
      case 'pub_start_date':
        return validators.required(intl.formatMessage({ id: 'GetStartedForm.pub_start_dateRequired' }));
      default:
        return undefined;
    }
  };

  const renderButtonChoice = (value, label, formRenderProps, fieldName) => (
    <button
      type="button"
      className={classNames(
        css.choiceButton,
        { [css.selected]: formRenderProps.values[fieldName] === value }
      )}
      onClick={() => {
        formRenderProps.form.change(fieldName, value);
        trackStepCompletion(step);
        setTimeout(() => setStep(step + 1), 200);
      }}
    >
      {label}
    </button>
  );

  const renderFinalStep = () => {
    return (
      <div className={css.finalStepContainer}>
        <FieldTextInput
          className={css.inputField}
          type="email"
          id="email"
          name="email"
          autoComplete="email"
          label="Email Address"
          placeholder="Enter your email address"
          validate={validators.composeValidators(
            validators.required("Email is required"),
            validators.emailFormatValid("Please enter a valid email address")
          )}
        />
        <FieldTextInput
          className={css.inputField}
          type="password"
          id="password"
          name="password"
          autoComplete="new-password"
          label="Password"
          placeholder="Enter your password"
          validate={validators.composeValidators(
            validators.required("Password is required"),
            validators.minLength("Password must be at least 8 characters", 8)
          )}
        />
        <div className={css.termsAndConditions}>
          {termsAndConditions}
        </div>
        <Button
          type="submit"
          inProgress={isSubmitting}
          disabled={isSubmitting}
          className={css.submitButton}
        >
          Submit & Search
        </Button>
      </div>
    );
  };

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      initialValues={initialValues}
      onSubmit={(values) => {
        if (step < 6) {
          trackStepCompletion(step);
          setInitialValues({ ...initialValues, ...values });
          setStep(step + 1);
        } else {
          trackStepCompletion(6);
          onSubmit({ ...initialValues, ...values });
        }
      }}
      render={(formRenderProps) => {
        const { handleSubmit, invalid, submitting, values } = formRenderProps;

        const submitDisabled = invalid || submitting;
        const submitInProgress = submitting;
        const classes = classNames(props.className, css.root);

        let content;
        let cardTitle;
        let stepCounter;

        switch (step) {
          case 0:
            cardTitle = "Select Practice Type";
            stepCounter = `Step 1 of 7`;
            content = (
              <div className={css.checkboxGroup}>
                {practiceTypeOptions.map((option) => (
                  <FieldCheckbox
                    key={option.value}
                    name="pub_practice_types"
                    id={`pub_practice_types_${option.value}`}
                    label={option.label}
                    value={option.value}
                    validate={handleFieldValidation('pub_practice_types')}
                  />
                ))}
              </div>
            );
            break;

          case 1:
            cardTitle = intl.formatMessage({ id: 'GetStartedForm.primaryStateOfPractice' });
            stepCounter = intl.formatMessage(
              { id: 'GetStartedForm.cardTitleStep' },
              { step: 2, total: 6 }
            );
            content = (
              <div className={css.stateSelectContainer}>
                <label className={css.selectLabel}>
                  {intl.formatMessage({ id: 'GetStartedForm.primaryStateOfPractice' })}
                </label>
                <select
                  name="pub_State"
                  id="pub_State"
                  className={css.nativeSelect}
                  onChange={(e) => formRenderProps.form.change('pub_State', e.target.value)}
                  value={formRenderProps.values.pub_State || ''}
                >
                  <option value="">
                    {intl.formatMessage({ id: 'GetStartedForm.selectState' })}
                  </option>
                  {US_STATES.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>
            );
            break;

          case 2:
            cardTitle = intl.formatMessage({ id: 'GetStartedForm.pub_LicenseLabel' });
            stepCounter = intl.formatMessage(
              { id: 'GetStartedForm.cardTitleStep' },
              { step: 3, total: 6 }
            );
            content = (
              <div className={css.buttonChoices}>
                {renderButtonChoice('NP', 'Nurse Practitioner', formRenderProps, 'pub_License')}
                {renderButtonChoice('PA', 'Physician Assistant', formRenderProps, 'pub_License')}
                {renderButtonChoice('MD', 'Medical Doctor', formRenderProps, 'pub_License')}
                {renderButtonChoice('Practice Owner', 'Practice Owner', formRenderProps, 'pub_License')}
              </div>
            );
            break;

          case 3:
            cardTitle = intl.formatMessage({ id: 'GetStartedForm.pub_controlled_substancesLabel' });
            stepCounter = intl.formatMessage(
              { id: 'GetStartedForm.cardTitleStep' },
              { step: 4, total: 6 }
            );
            content = (
              <div className={css.buttonChoices}>
                {renderButtonChoice('yes', 'Yes', formRenderProps, 'pub_controlled_substances')}
                {renderButtonChoice('no', 'No', formRenderProps, 'pub_controlled_substances')}
              </div>
            );
            break;

          case 4:
            cardTitle = intl.formatMessage({ id: 'GetStartedForm.pub_start_dateLabel' });
            stepCounter = intl.formatMessage(
              { id: 'GetStartedForm.cardTitleStep' },
              { step: 5, total: 6 }
            );
            content = (
              <div className={css.buttonChoices}>
                {renderButtonChoice(START_DATE.IMMEDIATELY, 'Immediately', formRenderProps, 'pub_start_date')}
                {renderButtonChoice(START_DATE.ONE_TO_TWO_MONTHS, '1-2 Months', formRenderProps, 'pub_start_date')}
                {renderButtonChoice(START_DATE.THREE_TO_SIX_MONTHS, '3-6 Months', formRenderProps, 'pub_start_date')}
              </div>
            );
            break;

          case 5:
            cardTitle = "Complete Your Personal Information";
            stepCounter = `Step 6 of 7`;
            content = (
              <div className={css.personalInfo}>
                <FieldTextInput
                  className={css.inputField}
                  type="text"
                  id="fname"
                  name="fname"
                  autoComplete="given-name"
                  label="First Name"
                  placeholder="Enter your first name"
                  validate={validators.required("First name is required")}
                />
                <FieldTextInput
                  className={css.inputField}
                  type="text"
                  id="lname"
                  name="lname"
                  autoComplete="family-name"
                  label="Last Name"
                  placeholder="Enter your last name"
                  validate={validators.required("Last name is required")}
                />
                <div className={css.phoneFieldContainer}>
                  <FieldTextInput
                    className={css.inputField}
                    type="tel"
                    id="phone"
                    name="phone"
                    autoComplete="tel"
                    label="Phone Number (Optional)"
                    placeholder="Example: 123-456-7890"
                    validate={validatePhone}
                    parse={(value) => {
                      if (!value) return value;
                      const digits = value.replace(/\D/g, '');
                      if (digits.length <= 3) return digits;
                      if (digits.length <= 6) return `${digits.slice(0,3)}-${digits.slice(3)}`;
                      return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6,10)}`;
                    }}
                  />
                  <SmsConsentDisclaimer />
                </div>
              </div>
            );
            break;

          case 6:
            cardTitle = "Complete Your Registration";
            stepCounter = `Step 7 of 7`;
            content = renderFinalStep();
            break;

          default:
            cardTitle = '';
            content = null;
            stepCounter = null;
        }

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {renderCard(content, cardTitle, stepCounter, step, setStep)}
            <div className={css.bottomWrapper}>
              {(step === 0 || step === 1 || step === 5) && (
                <PrimaryButton 
                  type="submit" 
                  inProgress={submitInProgress} 
                  disabled={submitDisabled}
                >
                  Next
                </PrimaryButton>
              )}
            </div>
            <div className={css.signInSection}>
              Already registered?{' '}
              <Link to="/login" className={css.signInLink}>
                Sign In
              </Link>
            </div>
          </Form>
        );
      }}
    />
  );
};

GetStartedFormComponent.defaultProps = {
  userTypes: [],
  userFields: [],
  isSubmitting: false,
};

GetStartedFormComponent.propTypes = {
  userTypes: propTypes.userTypes,
  userFields: propTypes.listingFields,
  intl: intlShape.isRequired,
  onSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  termsAndConditions: PropTypes.node.isRequired,
  isSubmitting: PropTypes.bool,
};

// Enhanced GetStartedForm with injectIntl
const GetStartedForm = compose(injectIntl)(GetStartedFormComponent);

export default GetStartedForm;
